import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromCustomersReducer from '@appStore/customers/reducers/customers.reducer';
import * as fromCustomerUsersReducer from '@appStore/customers/reducers/customer-users.reducer';
import * as fromCustomerInvitationReducer from '@appStore/customers/reducers/customer-invitations.reducer';
import * as fromCustomerRolesReducer from '@appStore/customers/reducers/customer-roles.reducer';
import * as fromCustomerManagerCompaniesReducer from '@appStore/customers/reducers/customer-manager-companies.reducer';
import * as fromCustomersTableReducer from '@appStore/customers/reducers/customers-table.reducer';
import * as fromRoot from '../../../reducers';

// feature state
export interface CustomersState {
  customers: fromCustomersReducer.State;
  customerUsers: fromCustomerUsersReducer.State;
  customerInvitations: fromCustomerInvitationReducer.State;
  customerRoles: fromCustomerRolesReducer.State;
  customerManagerCompanies: fromCustomerManagerCompaniesReducer.State;
  customersTable: fromCustomersTableReducer.State;
}

export interface State extends fromRoot.State {
  customers: CustomersState;
}

// reducers
export const reducers: ActionReducerMap<CustomersState> = {
  customers: fromCustomersReducer.reducer,
  customerUsers: fromCustomerUsersReducer.reducer,
  customerInvitations: fromCustomerInvitationReducer.reducer,
  customerRoles: fromCustomerRolesReducer.reducer,
  customerManagerCompanies: fromCustomerManagerCompaniesReducer.reducer,
  customersTable: fromCustomersTableReducer.reducer
};

// CUSTOMERS
export const getState = createFeatureSelector<CustomersState>('customers');

// Customers
export const getCustomerState = createSelector(
  getState,
  (state: CustomersState) => state.customers
);
export const getCustomers = createSelector(
  getCustomerState,
  fromCustomersReducer.getCustomers
);
export const getCustomersLoaded = createSelector(
  getCustomerState,
  fromCustomersReducer.getLoaded
);
export const getCustomerSelected = createSelector(
  getCustomerState,
  fromCustomersReducer.getSelectedCustomer
);
export const getSelectedCustomersTab = createSelector(
  getCustomerState,
  fromCustomersReducer.getSelectedTab
);

export const getCustomersUserAccess = createSelector(
  getCustomerState,
  fromCustomersReducer.getCustomersUserAccess
);
export const getCustomersUserAccessLoaded = createSelector(
  getCustomerState,
  fromCustomersReducer.getCustomersUserAccessLoaded
);
export const getCustomersUserAccessSelected = createSelector(
  getCustomerState,
  fromCustomersReducer.getCustomersUserAccessSelected
);

export const getCustomersUsers = createSelector(
  getCustomerState,
  fromCustomersReducer.getCustomersUsers
);
export const getCustomersUsersLoaded = createSelector(
  getCustomerState,
  fromCustomersReducer.getCustomersUsersLoaded
);

export const getCustomersInvitations = createSelector(
  getCustomerState,
  fromCustomersReducer.getCustomersInvitations
);
export const getCustomersInvitationsLoaded = createSelector(
  getCustomerState,
  fromCustomersReducer.getCustomersInvitationsLoaded
);

export const getUserSelected = createSelector(
  getCustomerState,
  fromCustomersReducer.getSelectedUser
);
export const getSelectedUserAccess = createSelector(
  getCustomerState,
  fromCustomersReducer.getSelectedUserAccess
);
export const getSelectedUserAccessLoaded = createSelector(
  getCustomerState,
  fromCustomersReducer.getSelectedUserAccessLoaded
);
export const getSelectedUserCustomersSelected = createSelector(
  getCustomerState,
  fromCustomersReducer.getSelectedUserCustomersSelected
);

// Customers and CustomerUsers Filter
export const getCustomersFilter = createSelector(
  getCustomerState,
  fromCustomersReducer.getFilter
);
export const getCustomersFilterLoaded = createSelector(
  getCustomerState,
  fromCustomersReducer.getFilterLoaded
);
export const getCustomersUsersFilter = createSelector(
  getCustomerState,
  fromCustomersReducer.getUsersFilter
);
export const getCustomersUsersFilterLoaded = createSelector(
  getCustomerState,
  fromCustomersReducer.getUsersFilterLoaded
);

// CustomerUsers
export const getCustomerUsersState = createSelector(
  getState,
  state => state.customerUsers
);
export const getCustomerUsers = createSelector(
  getCustomerUsersState,
  fromCustomerUsersReducer.getCustomerUsers
);
export const getCustomerUsersLoaded = createSelector(
  getCustomerUsersState,
  fromCustomerUsersReducer.getCustomerUsersLoaded
);
export const getCustomerUsersSelectedTab = createSelector(
  getCustomerUsersState,
  fromCustomerUsersReducer.getCustomerUsersSelectedTab
);
export const getCustomerUserUpdated = createSelector(
  getCustomerUsersState,
  fromCustomerUsersReducer.getCustomerUserUpdated
);
export const getSetPrimaryCustomerUserUpdated = createSelector(
  getCustomerUsersState,
  fromCustomerUsersReducer.getSetPrimaryCustomerUserUpdated
);
export const getCustomerUserRemoved = createSelector(
  getCustomerUsersState,
  fromCustomerUsersReducer.getCustomerUserRemoved
);
export const getCustomerUserCreated = createSelector(
  getCustomerUsersState,
  fromCustomerUsersReducer.getCustomerUserCreated
);
export const getCustomerUserBulkUpdated = createSelector(
  getCustomerUsersState,
  fromCustomerUsersReducer.getCustomerUserBulkUpdated
);
export const getCustomerUserBulkRemoved = createSelector(
  getCustomerUsersState,
  fromCustomerUsersReducer.getCustomerUserBulkRemoved
);

// CustomerInvitations
export const getCustomerInvitationsState = createSelector(
  getState,
  state => state.customerInvitations
);
export const getCustomerInvitations = createSelector(
  getCustomerInvitationsState,
  fromCustomerInvitationReducer.getCustomerInvitations
);
export const getCustomerInvitationsLoaded = createSelector(
  getCustomerInvitationsState,
  fromCustomerInvitationReducer.getCustomerInvitationsLoaded
);
export const getCustomerInvitationsCreated = createSelector(
  getCustomerInvitationsState,
  fromCustomerInvitationReducer.getCustomerInvitationsCreated
);

// CustomerManagerCompanies
export const getCustomersManagerCompaniesState = createSelector(
  getState,
  state => state.customerManagerCompanies
);

export const getCustomerManagerCompanies = createSelector(
  getCustomersManagerCompaniesState,
  fromCustomerManagerCompaniesReducer.getCustomerManagerCompanies
);

export const getCustomerManagerCompaniesLoaded = createSelector(
  getCustomersManagerCompaniesState,
  fromCustomerManagerCompaniesReducer.getCustomerManagerCompaniesLoaded
);

export const getCustomerManagerCompanyDeleted = createSelector(
  getCustomersManagerCompaniesState,
  fromCustomerManagerCompaniesReducer.getCustomerManagerCompanyDeleted
);
export const getCustomerManagerCompanyUpdated = createSelector(
  getCustomersManagerCompaniesState,
  fromCustomerManagerCompaniesReducer.getCustomerManagerCompanyUpdated
);

// CustomersRoles
export const getCustomerRolesState = createSelector(
  getState,
  state => state.customerRoles
);
export const getCustomersRoles = createSelector(
  getCustomerRolesState,
  fromCustomerRolesReducer.getCustomerRoles
);
export const getCustomersRolesLoaded = createSelector(
  getCustomerRolesState,
  fromCustomerRolesReducer.getCustomerRolesLoaded
);

// CustomersTable
export const getCustomersTableState = createSelector(
  getState,
  state => state.customersTable
);

export const getCustomerUsersPageData = createSelector(
  getCustomersTableState,
  fromCustomersTableReducer.getCustomerUsersTableState
);

export const getCustomerInivitationsPageData = createSelector(
  getCustomersTableState,
  fromCustomersTableReducer.getCustomerInvitationsTableState
);

export const getCustomerPageData = createSelector(
  getCustomersTableState,
  fromCustomersTableReducer.getCustomerCustomersTableState
);

export const getUserCustomersPageData = createSelector(
  getCustomersTableState,
  fromCustomersTableReducer.getCustomerUserCustomersTableState
);

//External Organisations
export const getExternalOrganisations = createSelector(
  getCustomerState,
  fromCustomersReducer.getCustomersExternalOrganisations
);
export const getExternalOrganisationsLoaded = createSelector(
  getCustomerState,
  fromCustomersReducer.getCustomersExternalOrganisationsLoaded
);

export const getLockedusers = createSelector(
  getCustomerState,
  fromCustomersReducer.getLockedusers
);